/**
 * Debug panel for diagnosing normal / shading issues on morphed mannequins.
 *
 * Visible when debug mode is active and the user is in pose mode.
 * Each toggle is independently selectable — they can be combined freely.
 * All default to off so the panel is purely opt-in.
 *
 * ## Context
 *
 * Normals are CPU-recomputed from the fully morphed+skinned geometry by
 * default for all mannequin materials (see MannequinRenderer.tsx). The
 * toggles below provide additional diagnostic tools that layer on top.
 */

import { useCallback } from "react";
import { useStore } from "../app/store.ts";
import type { ShadingDebugFlags } from "../app/storeTypes.ts";
import { DraggablePanel } from "../ui/DraggablePanel.tsx";
import { usePanelSelection } from "../ui/floatingPanelStore.ts";
import {
  COLOR_TEXT_DIM,
  COLOR_TEXT_FAINT,
  COLOR_TEXT_SECONDARY,
  Z_DEBUG,
} from "../ui/tokens.ts";

interface FlagDef {
  key: keyof ShadingDebugFlags;
  label: string;
  hint: string;
}

/** Separator sentinel used for visual grouping in the panel. */
const SEP = "---" as const;

type FlagEntry = FlagDef | typeof SEP;

const FLAGS: FlagEntry[] = [
  // --- Shader patches (GPU-safe, layer on top of CPU-recomputed normals) ---
  {
    key: "screenSpaceNormals",
    label: "Screen-Space Normals",
    hint: "override fragment normal with dFdx/dFdy of morphed surface — geometry-accurate, faceted",
  },
  {
    key: "blendedNormals",
    label: "Blended Normals (50/50)",
    hint: "blend vertex normals with screen-space normals at 50/50 — geometry-correct direction with reduced faceting",
  },
  SEP,
  // --- Material-level overrides ---
  {
    key: "disableNormalMap",
    label: "Disable Normal Map",
    hint: "strip normal map from material",
  },
  {
    key: "flatShading",
    label: "Flat Shading",
    hint: "force per-face normals via material.flatShading",
  },
  SEP,
  // --- Diagnostic visualizations ---
  {
    key: "normalDirectionViz",
    label: "Normal Direction",
    hint: "R=X G=Y B=Z world-space normal (mapped 0-1) — screenshot for exact values",
  },
  {
    key: "gpuNormalBufferViz",
    label: "GPU Normal Buffer",
    hint: "raw GPU compute output as vertex colors — isolates compute vs material bugs",
  },
  {
    key: "morphInfluenceHeatmap",
    label: "Morph Influence",
    hint: "active morph target weights as heatmap — diagnose corrective shape key issues",
  },
  {
    key: "boneInfluenceViz",
    label: "Bone Influence",
    hint: "per-vertex bone weight heatmap for selected bone (red = 1.0, blue = 0.0)",
  },
  {
    key: "normalMapViz",
    label: "Normal Map",
    hint: "render the baked normal map texture as surface color (unlit) — verify what the normal map contributes",
  },
  {
    key: "uvIslandViz",
    label: "UV Islands",
    hint: "color each UV island a distinct random color — pinpoint UV seam boundaries on the mesh surface",
  },
  SEP,
  // --- Deformation trials ---
  {
    key: "dqsSkinning",
    label: "Dual Quaternion Skinning",
    hint: "blend LBS with DQS to preserve volume at bent/twisted joints — trial mode",
  },
  {
    key: "correctiveSmooth",
    label: "Corrective Smooth",
    hint: "Laplacian delta smooth post-skinning — reduces LBS artifacts while preserving silhouette — trial mode",
  },
];

export function ShadingDebugPanel() {
  const debugEnabled = useStore((s) => s.debugEnabled);
  const panelOpen = useStore((s) => s.debugPanelOpen.shadingDebug);
  const flags = useStore((s) => s.shadingDebugFlags);
  const toggle = useStore((s) => s.toggleShadingDebugFlag);
  const togglePanel = useStore((s) => s.toggleDebugPanel);
  const sel = usePanelSelection("debug-shading", () =>
    togglePanel("shadingDebug"),
  );

  const copySnapshot = useCallback(() => {
    const diag = window.__shadingDiag;
    if (!diag?.snapshot) return;
    const snap = diag.snapshot();
    navigator.clipboard
      .writeText(JSON.stringify(snap, null, 2))
      .catch(() => {});
  }, []);

  if (!debugEnabled || !panelOpen) return null;

  return (
    <DraggablePanel
      id="debug-shading"
      defaultPosition={{ bottom: 8, right: 230 }}
      zIndex={Z_DEBUG}
      collapsible
      selectionColor="#ff0"
      {...sel}
      style={{ minWidth: 220 }}
      header={
        <span
          style={{
            color: "#ff0",
            fontWeight: "bold",
            fontFamily: "monospace",
            fontSize: 10,
          }}
        >
          SHADING DEBUG
        </span>
      }
    >
      <div
        style={{
          background: "rgba(0,0,0,0.85)",
          color: COLOR_TEXT_SECONDARY,
          fontFamily: "monospace",
          fontSize: 10,
          padding: "6px 10px",
          borderRadius: "0 0 6px 6px",
          pointerEvents: "auto",
          lineHeight: 1.8,
        }}
      >
        {FLAGS.map((entry, i) => {
          if (entry === SEP) {
            return (
              <div
                key={`sep-${i}`}
                style={{
                  borderTop: "1px solid #333",
                  margin: "3px 0",
                }}
              />
            );
          }
          return (
            <FlagRow
              key={entry.key}
              def={entry}
              enabled={flags[entry.key]}
              onToggle={toggle}
            />
          );
        })}
        <div
          style={{
            color: COLOR_TEXT_DIM,
            marginTop: 4,
            borderTop: "1px solid #333",
            paddingTop: 4,
            fontSize: 9,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <span>normals GPU-recomputed from deformed geometry</span>
          <button
            onClick={copySnapshot}
            data-no-drag
            style={{
              background: "#333",
              color: "#0f0",
              border: "1px solid #555",
              borderRadius: 3,
              padding: "1px 6px",
              fontSize: 9,
              fontFamily: "monospace",
              cursor: "pointer",
            }}
            title="Copy pipeline state snapshot to clipboard (JSON)"
          >
            SNAPSHOT
          </button>
        </div>
      </div>
    </DraggablePanel>
  );
}

function FlagRow({
  def,
  enabled,
  onToggle,
}: {
  def: FlagDef;
  enabled: boolean;
  onToggle: (flag: keyof ShadingDebugFlags) => void;
}) {
  return (
    <label
      style={{
        display: "flex",
        alignItems: "center",
        gap: 6,
        cursor: "pointer",
        color: enabled ? "#0f0" : COLOR_TEXT_FAINT,
        userSelect: "none",
      }}
      title={def.hint}
    >
      <input
        type="checkbox"
        checked={enabled}
        onChange={() => onToggle(def.key)}
        style={{ accentColor: "#0f0", cursor: "pointer" }}
        data-no-drag
      />
      <span>{def.label}</span>
    </label>
  );
}
